/**
 * Evals & Model Governance (ADR-0011) — golden datasets and the regression /
 * approval gate. A blocked gate opens a HITL review task (visible in the AI
 * review queue). Admin/All-scoped (child of the Intelligence nav item).
 */
import { useState } from 'react';

import { Card } from '../components/Card';
import { DataTable, type Column } from '../components/DataTable';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { PageHeader } from '../components/PageHeader';
import { StatusBadge } from '../components/StatusBadge';
import { useShellSlot } from '../components/shellContext';
import {
  type GateResult,
  type GoldenDataset,
  runRegressionGate,
  useGoldenDatasets,
} from '../hooks/useAgentPlatform';

export function EvalsPage() {
  useShellSlot({ breadcrumb: 'Evals' });
  const { items, available, isLoading, refresh } = useGoldenDatasets();

  const [capability, setCapability] = useState('propensity');
  const [version, setVersion] = useState('v2');
  const [gate, setGate] = useState<GateResult | null>(null);
  const [running, setRunning] = useState(false);

  const onGate = () => {
    if (!capability.trim() || !version.trim()) return;
    setRunning(true);
    runRegressionGate(capability, version)
      .then(setGate)
      .catch(() => setGate(null))
      .finally(() => setRunning(false));
  };

  const cols: Column<GoldenDataset>[] = [
    { key: 'name', header: 'Dataset', render: (d) => <strong>{d.name}</strong> },
    { key: 'capability', header: 'Capability', render: (d) => d.capability },
    {
      key: 'examples',
      header: 'Examples',
      align: 'right',
      render: (d) => d.examples?.length ?? 0,
    },
  ];

  return (
    <>
      <PageHeader
        title="Evals & Model Governance"
        subtitle="Golden datasets and the regression/approval gate. A blocked gate routes to human sign-off."
        actions={
          <button className="btn btn-secondary btn-sm" onClick={refresh} disabled={isLoading}>
            Refresh
          </button>
        }
      />

      {!available && (
        <p role="status" style={{ color: '#8a6d3b', fontSize: '0.86rem', marginBottom: 12 }}>
          Evals service unavailable — showing an empty state.
        </p>
      )}

      <Card title="Golden datasets" padded={false} style={{ marginBottom: 16 }}>
        {isLoading && !items.length ? (
          <div style={{ padding: 24 }}>
            <LoadingIndicator text="Loading datasets…" />
          </div>
        ) : (
          <DataTable
            columns={cols}
            rows={items}
            rowKey={(d) => d.id}
            emptyTitle="No datasets"
            emptyDescription="Create golden datasets to evaluate model versions."
          />
        )}
      </Card>

      <Card title="Regression / approval gate">
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <input
            value={capability}
            onChange={(e) => setCapability(e.target.value)}
            placeholder="capability"
            style={{ padding: '8px 10px', border: '1px solid #d9d4cc', borderRadius: 8 }}
          />
          <input
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder="candidate version (try v2-regress)"
            style={{ minWidth: 240, padding: '8px 10px', border: '1px solid #d9d4cc', borderRadius: 8 }}
          />
          <button className="btn btn-primary btn-sm" onClick={onGate} disabled={running}>
            Run gate
          </button>
        </div>
        {running ? (
          <LoadingIndicator text="Evaluating…" />
        ) : gate === null ? (
          <p style={{ color: '#6b6459', fontSize: '0.86rem' }}>
            Run the gate to compare a candidate model to the champion baseline.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <StatusBadge
                status={gate.passed ? 'success' : 'danger'}
                label={gate.passed ? 'passed' : 'blocked'}
              />
              <span style={{ fontSize: '0.9rem' }}>
                {gate.capability} · {gate.candidate_version} vs {gate.champion_version}
              </span>
            </div>
            <div style={{ fontSize: '0.86rem', color: '#6b6459' }}>
              Bias review: {gate.bias_review_status} · Approval gate: {gate.approval_gate}
              {gate.review_task_id ? ` · HITL task: ${gate.review_task_id}` : ''}
            </div>
            {!gate.passed && (
              <p style={{ fontSize: '0.86rem' }}>
                Blocked — a human review task was opened. It appears in the AI review queue for sign-off.
              </p>
            )}
          </div>
        )}
      </Card>
    </>
  );
}
